export interface HealthResponse {
  status: "ok" | "degraded";
  service: string;
  environment: string;
}

export interface SessionResponse {
  user_id: string;
  role: "user" | "manager" | "owner";
  must_change_password: boolean;
  mfa_verified: boolean;
  next_action: "replace_password" | "enroll_mfa" | "verify_mfa" | "ready";
}

export interface MFAEnrollmentResponse {
  secret: string;
  provisioning_uri: string;
}

export interface MFAVerificationResponse extends SessionResponse {
  recovery_codes: string[];
}

export interface DepartmentSummary {
  id: string;
  code: string;
  name: string;
  is_active?: boolean;
  status?: "active" | "inactive";
}

export interface DepartmentDirectoryItem extends DepartmentSummary {
  is_active: boolean;
  status: "active" | "inactive";
  active_user_count: number;
  active_grant_count: number;
  active_quota_count: number;
}

export interface DepartmentImpact {
  active_user_count: number;
  active_grant_count: number;
  active_quota_count: number;
}

export interface DepartmentFilters {
  search?: string;
  status?: "active" | "inactive";
}

export interface CurrentAccountProfile {
  id: string;
  username: string;
  display_name: string;
  role: UserRole;
  primary_department: DepartmentSummary | null;
  account_state: "active" | "disabled" | "archived";
  password_state: "active" | "temporary";
  temporary_password_expires_at: string | null;
  mfa_state: "not_required" | "enrollment_required" | "enabled";
}

export type UserRole = "user" | "manager" | "owner";
export type ResourceType = "department" | "store" | "brand" | "site" | "dataset";

export interface ManagedUser {
  id: string;
  username: string;
  display_name: string;
  role: UserRole;
  primary_department_id: string | null;
  is_active: boolean;
  must_change_password: boolean;
  account_state: "active" | "disabled" | "archived";
}

export interface TemporaryCredential extends ManagedUser {
  temporary_password: string;
  temporary_password_expires_at: string;
}

export interface ManagedUserFilters {
  search?: string;
  role?: UserRole;
  departmentId?: string;
  state?: ManagedUser["account_state"];
}

export interface ResourceGrant {
  id: string;
  user_id: string;
  resource_type: ResourceType;
  resource_id: string;
}

export interface ManagedImportProfile {
  profile_id: string;
  version_id: string;
  code: string;
  display_name: string;
  report_type: ReportType;
  version: number;
  state: "draft" | "published" | "disabled";
  definition_sha256: string;
  definition: Record<string, unknown>;
}

export interface Quota {
  id: string;
  subject_type: "user" | "department";
  subject_id: string;
  period: "daily" | "monthly";
  request_limit: number | null;
  token_limit: number | null;
  image_limit: number | null;
  cost_limit: string | null;
  concurrency_limit: number;
  rate_limit_per_minute: number;
  effective_from: string;
  effective_until: string | null;
  is_active: boolean;
}

export interface QuotaDraft {
  request_limit: number | null;
  token_limit: number | null;
  image_limit: number | null;
  cost_limit: string | null;
  concurrency_limit: number;
  rate_limit_per_minute: number;
}

export interface ModelProvider {
  id: string;
  code: string;
  display_name: string;
  adapter_type: string;
  state: "draft" | "active" | "disabled";
  endpoint_url: string | null;
  capabilities: string[];
  contract_metadata: Record<string, unknown>;
  credential_configured: boolean;
  credential_version: number;
  connection_config_version: number;
  verification_state: "untested" | "verified" | "failed" | "stale";
  verification_code: string | null;
  verified_at: string | null;
  adapter_options: Record<string, unknown>;
  enabled_model_count: number;
}

export interface ProviderAdapterDescriptor {
  code: string;
  display_name: string;
  protocol: string;
  capabilities: string[];
  endpoint_supported: boolean;
  endpoint_required: boolean;
  discovery_supported: boolean;
  manual_models_supported: boolean;
  credential_fields: Array<{
    name: string;
    label: string;
    secret: boolean;
    required: boolean;
  }>;
  option_fields: string[];
}

export interface ProviderReadiness {
  adapter_registered: boolean;
  credentials_ready: boolean;
  latest_version_verified: boolean;
  enabled_model_count: number;
  active_policy: boolean;
  ready: boolean;
  missing: string[];
}

export interface ProviderModel {
  id: string;
  provider_id: string;
  model_id: string;
  display_name: string;
  state: "disabled" | "enabled";
  source: "discovered" | "manual";
  capabilities: string[];
  context_window: number | null;
  max_output_tokens: number | null;
  pricing_metadata: Record<string, unknown>;
  pricing_version: string;
  revision: number;
  validated: boolean;
  validated_at: string | null;
  discovered_at: string | null;
}

export interface ModelRouteCandidate {
  id: string;
  provider_model_id: string;
  provider_id: string;
  provider_code: string;
  model_id: string;
  position: number;
}

export interface ModelRouteVersion {
  id: string;
  route_id: string;
  version: number;
  state: "draft" | "active" | "disabled";
  candidates: ModelRouteCandidate[];
  published_at: string | null;
  diagnostics: string[];
}

export interface ModelRoute {
  id: string;
  code: string;
  display_name: string;
  capability: "text" | "vision" | "image";
  description: string;
  versions: ModelRouteVersion[];
}

export interface OperationalModelRoute {
  code: string;
  display_name: string;
  capability: "text" | "vision" | "image";
}

export interface ProviderPolicy {
  id: string;
  provider_id: string;
  version: number;
  state: "draft" | "active" | "disabled";
  allowed_classifications: Array<"internal" | "confidential" | "restricted">;
  allowed_capabilities: string[];
  allowed_regions: string[];
  max_cost_per_run: string;
  policy_document: Record<string, unknown>;
}

export interface AuditEvent {
  event_id: string;
  chain_sequence: number;
  occurred_at: string;
  actor_user_id: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  outcome: "success" | "failure" | "denied";
  correlation_id: string;
  source_address: string | null;
  safe_metadata: Record<string, unknown>;
}

export interface UsageReport {
  started_from: string;
  started_until: string;
  attempts: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  image_units: number;
  total_cost: string;
}

export interface GovernanceHealth {
  status: "ok" | "degraded";
  checked_at: string;
  database: "ok";
  audit_chain_valid: boolean;
  audit_events: number;
  latest_audit_at: string | null;
  providers: Record<string, number>;
  runs: Record<string, number>;
  usage: Record<string, number>;
  operational: {
    status: "ok" | "degraded";
    services: Record<string, string>;
    queue: Record<string, number | null>;
    storage: Record<string, number>;
    latency: Record<string, number | null>;
    errors: Record<string, number>;
    concurrency: Record<string, number>;
    usage: Record<string, number | string>;
    alerts: string[];
  };
}

export const AUTH_SESSION_EXPIRED_EVENT = "ecom-assistant:session-expired";

export interface ImportProfile {
  id: string;
  code: string;
  display_name: string;
  report_type: ReportType;
  version: number;
  definition: {
    default_source_variant: string;
    source_variants: Array<{
      code: string;
      required_fields?: string[];
      optional_fields?: string[];
    }>;
  };
  sample_filename: string;
  sample_media_type: string;
}

export type ReportType = "sales_traffic" | "advertising" | "inventory_lead_time";

export interface DataSource {
  id: string;
  scope_id: string;
  code: string;
  display_name: string;
  source_context: Record<string, unknown>;
}

export interface RawUpload {
  id: string;
  original_filename: string;
  processing_state: string;
  duplicate: boolean;
}

export interface ValidationIssue {
  code: string;
  report_type: string;
  message: string;
  blocking: boolean;
  row_number: number | null;
  field: string | null;
  entity_key: string | null;
}

export interface ReportSet {
  id: string;
  scope_id: string;
  store_code: string;
  site: string;
  period_start: string;
  period_end: string;
  analysis_config: Record<string, unknown>;
  state: string;
  validation_summary: Record<string, unknown>;
  imports: Array<Record<string, unknown>>;
}

export interface ReportSetImportResponse {
  blocked: boolean;
  report_set: ReportSet | null;
  issues: ValidationIssue[];
}

export interface RunStatus {
  id: string;
  state: string;
  execution_mode: string;
  attempt_count: number;
  last_event_seq: number;
  safe_error: Record<string, unknown> | null;
  snapshot_id: string | null;
  artifact_id: string | null;
}

export interface TaskCard {
  code: string;
  display_name: string;
  description: string;
  execution_mode: "skill" | "workflow" | "agent";
  target: string;
  input_schema: Record<string, unknown>;
  resource_scope_types: Array<"department" | "store" | "brand" | "site" | "dataset">;
}

export interface EffectiveCapability {
  kind: "skill" | "workflow";
  code: string;
  version: number;
  display_name: string;
  description: string;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  required_resource_scopes: ResourceType[];
  intended_usage: "direct" | "agent_internal";
  used_by_agents: string[];
}

export interface CapabilityCatalog {
  skills: EffectiveCapability[];
  workflows: EffectiveCapability[];
  authoring_enabled: false;
}

export interface ExecutionRun {
  id: string;
  requested_by_user_id: string;
  state:
    "accepted" | "queued" | "running" | "awaiting_approval" | "completed" | "failed" | "cancelled";
  execution_mode: "skill" | "workflow" | "agent";
  target: string;
  task_card: string | null;
  scope_type: string;
  scope_id: string;
  attempt_count: number;
  last_event_seq: number;
  cancel_requested: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface ExecutionRunStep {
  id: string;
  step_key: string;
  sequence: number;
  state: string;
  attempt_count: number;
  max_attempts: number;
  output_payload: Record<string, unknown> | null;
  safe_error: Record<string, unknown> | null;
}

export interface ExecutionEvent {
  seq: number;
  type: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

export interface ExecutionApproval {
  approval_key: string;
  required_role: "manager" | "owner";
  decision: "pending" | "approved" | "rejected";
  requested_at: string;
  decided_at: string | null;
  decision_note: string | null;
  decision_context: Record<string, unknown>;
}

export interface AgentPlanStep {
  sequence: number;
  state: string;
  rationale_summary: string;
  planned_action: Record<string, unknown>;
  tool_calls: Array<{
    sequence: number;
    tool_class: string;
    tool_name: string;
    allowed: boolean;
    policy_decision: Record<string, unknown>;
    output_summary: Record<string, unknown> | null;
  }>;
}

export interface ExecutionArtifact {
  id: string;
  artifact_type: string;
  media_type: string;
  byte_size: number;
  content_sha256: string;
  download_url: string;
}

export interface ExecutionRunDetail extends ExecutionRun {
  safe_error: Record<string, unknown> | null;
  steps: ExecutionRunStep[];
  events: ExecutionEvent[];
  approvals: ExecutionApproval[];
  agent_plan: AgentPlanStep[];
  artifacts: ExecutionArtifact[];
}

export interface WebSocketTicket {
  ticket: string;
  expires_at: string;
}

export interface WeeklySummary {
  snapshot_id: string;
  metrics: Record<string, unknown>;
  rule_version: string;
  calculation_version: string;
}

export interface Finding {
  finding_key: string;
  finding: Record<string, unknown>;
}

export interface ReviewResponse {
  id: string;
  revision: number;
  decision: "accepted" | "rejected";
  note: string | null;
  reviewed_by_user_id: string;
}

export interface AmazonSearchTerm {
  sku: string;
  search_term: string;
  impressions: number;
  clicks: number;
  spend: string;
  attributed_sales: string;
  attributed_orders: number;
  click_through_rate: string | null;
  conversion_rate: string | null;
  cost_per_click: string | null;
  acos: string | null;
  roas: string | null;
  currency: string;
  evidence_links: string[];
}

export interface AdvertisingCandidate {
  candidate: {
    candidate_id: string;
    candidate_type: "negative_keyword" | "expansion_keyword" | "budget" | "bid";
    sku: string;
    search_term: string | null;
    reason_code: string;
    recommendation: Record<string, string>;
    source_metrics: Record<string, unknown>;
    thresholds: Record<string, unknown>;
    comparisons: Record<string, string>;
    policy_version: string;
    advisory_only: true;
  };
  evidence_links: string[];
}

export interface AdvertisingAdvisory {
  advisory_only: true;
  policy_snapshot: Record<string, string | number>;
  candidates: AdvertisingCandidate[];
}

export interface NarrativeValidation {
  status: "accepted" | "rejected";
  narrative: Record<string, unknown> | null;
  conflicts: Array<Record<string, unknown>>;
}

export interface ReplenishmentPolicy {
  id: string;
  code: string;
  version: number;
  scope_type: "store";
  scope_id: string;
  state: "draft" | "published" | "disabled";
  formula: Record<string, unknown>;
  thresholds: Record<string, unknown>;
  units: Record<string, string>;
  safety_stock: Record<string, unknown>;
  promotion_factors: Record<string, unknown>;
}

export interface ReplenishmentRecommendation {
  id: string | null;
  artifact_id: string | null;
  status: "ready" | "insufficient_data";
  calculation_version: string;
  projected_stockout_at: string | null;
  recommended_order_at: string | null;
  recommended_quantity: string | null;
  quantity_unit: string | null;
  destination_id: string;
  logistics_recommendation: Record<string, unknown> | null;
  risk_level: string | null;
  intermediate_values: Record<string, unknown>;
  warnings: string[];
  missing_inputs: string[];
  source_references: string[];
  advisory_only: true;
  review_decision: "pending" | "accepted" | "rejected" | null;
}

export interface ReplenishmentRun {
  id: string;
  state: string;
  execution_mode: "workflow";
  attempt_count: number;
  safe_error: Record<string, unknown> | null;
}

export interface ReplenishmentPolicyDraft {
  code: string;
  scope_id: string;
  formula: Record<string, unknown>;
  thresholds: Record<string, unknown>;
  units: Record<string, string>;
  safety_stock: Record<string, unknown>;
  promotion_factors: Record<string, unknown>;
}

export interface ImagePilotRequest {
  id: string;
  request_key: string;
  run_id: string | null;
  source_raw_object_id: string;
  scope_type: "store" | "brand";
  scope_id: string;
  site_id: string;
  intended_use: string;
  dimensions: Record<string, unknown>;
  brand_rules: Record<string, unknown>;
  rights_context: Record<string, unknown>;
  prompt_template_version: string;
  user_inputs: Record<string, unknown>;
  state: "draft" | "processing" | "ready" | "failed";
  safe_error: Record<string, unknown> | null;
}

export interface ImagePilotCandidate {
  id: string;
  image_request_id: string;
  artifact_id: string;
  usage_entry_id: string | null;
  candidate_index: number;
  provider_id: string;
  model_name: string;
  prompt_snapshot: Record<string, unknown>;
  generation_parameters: Record<string, unknown>;
  estimated_cost: string;
  review_state: "pending" | "accepted" | "rejected";
}

export interface ImagePilotRun {
  id: string;
  state: string;
  request_id: string;
  image_request_id: string;
}

export interface ImageSourceDraft {
  data_source_id: string;
  request_key: string;
  site_id: string;
  intended_use: string;
  dimensions: Record<string, unknown>;
  brand_rules: Record<string, unknown>;
  rights_context: Record<string, unknown>;
  prompt_template_version: string;
  user_inputs: Record<string, unknown>;
}

export interface CatalogStore {
  id: string;
  label: string;
}

export interface CatalogResponsibleUser {
  id: string;
  display_name: string;
}

export interface CatalogContexts {
  stores: CatalogStore[];
  responsible_users: CatalogResponsibleUser[];
}

export interface CatalogProduct {
  id: string;
  sku: string;
  asin: string | null;
  name: string;
  responsible_user_id: string;
  has_image: boolean;
}

export interface CatalogProductPage {
  items: CatalogProduct[];
  total: number;
  page: number;
  page_size: 20 | 50 | 100;
}

export interface CatalogCsvIssue {
  row: number;
  field: string | null;
  code: string;
  message: string;
}

export interface CatalogImportResult {
  raw_object_id: string;
  imported_count: number;
  reactivated_count: number;
}

export interface CatalogImageImportItem {
  filename: string;
  sku: string;
  status: "success" | "failure";
  product_id: string | null;
  image_id: string | null;
  revision: number | null;
  error_code: string | null;
  message: string | null;
}

export interface CatalogImageMapping {
  file: File;
  sku: string;
}

export interface ApiErrorBody {
  detail?: string;
  request_id?: string;
  error?: { code?: string; message?: string; issues?: CatalogCsvIssue[] };
}

export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;
  readonly code?: string;
  readonly issues: CatalogCsvIssue[];

  constructor(
    message: string,
    status: number,
    requestId?: string,
    code?: string,
    issues: CatalogCsvIssue[] = [],
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
    this.code = code;
    this.issues = issues;
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetcher?: typeof fetch;
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  headers?: HeadersInit;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;

  constructor({
    baseUrl = "/api/v1",
    fetcher = (input, init) => fetch(input, init),
  }: ApiClientOptions = {}) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.fetcher = fetcher;
  }

  async getHealth(options: ApiRequestOptions = {}): Promise<HealthResponse> {
    return this.request<HealthResponse>("/health/ready", options);
  }

  async login(username: string, password: string): Promise<SessionResponse> {
    return this.json<SessionResponse>("/auth/login", "POST", { username, password });
  }

  async getSession(): Promise<SessionResponse> {
    return this.request<SessionResponse>("/auth/session", {}, false);
  }

  async replacePassword(newPassword: string): Promise<SessionResponse> {
    return this.json<SessionResponse>(
      "/auth/password",
      "POST",
      { new_password: newPassword },
      true,
    );
  }

  async beginMfaEnrollment(): Promise<MFAEnrollmentResponse> {
    return this.json<MFAEnrollmentResponse>("/auth/mfa/enrollment", "POST", {}, true);
  }

  async verifyMfa(code: string): Promise<MFAVerificationResponse> {
    return this.json<MFAVerificationResponse>("/auth/mfa/verify", "POST", { code }, true);
  }

  async logout(): Promise<void> {
    return this.json<void>("/auth/logout", "POST", {}, true);
  }

  async getCurrentAccount(): Promise<CurrentAccountProfile> {
    return this.request<CurrentAccountProfile>("/account/profile");
  }

  async changeCurrentPassword(payload: {
    current_password: string;
    new_password: string;
    step_up_code?: string;
  }): Promise<SessionResponse> {
    return this.json<SessionResponse>("/account/password", "POST", payload, true);
  }

  async recoverCurrentMfa(payload: {
    current_password: string;
    recovery_code: string;
  }): Promise<SessionResponse> {
    return this.json<SessionResponse>("/account/mfa/recovery", "POST", payload, true);
  }

  async listUsers(filters: ManagedUserFilters = {}): Promise<ManagedUser[]> {
    const query = new URLSearchParams();
    if (filters.search) query.set("search", filters.search);
    if (filters.role) query.set("role", filters.role);
    if (filters.departmentId) query.set("department_id", filters.departmentId);
    if (filters.state) query.set("state", filters.state);
    const suffix = query.size ? `?${query.toString()}` : "";
    return this.request<ManagedUser[]>(`/users${suffix}`);
  }

  async listUserDepartments(search = ""): Promise<DepartmentSummary[]> {
    const suffix = search ? `?search=${encodeURIComponent(search)}` : "";
    return this.request<DepartmentSummary[]>(`/users/departments${suffix}`);
  }

  async listDepartments(filters: DepartmentFilters = {}): Promise<DepartmentDirectoryItem[]> {
    const query = new URLSearchParams();
    if (filters.search) query.set("search", filters.search);
    if (filters.status) query.set("status", filters.status);
    const suffix = query.size ? `?${query.toString()}` : "";
    return this.request<DepartmentDirectoryItem[]>(`/departments${suffix}`);
  }

  async createDepartment(payload: { name: string; code: string }): Promise<DepartmentSummary> {
    return this.json<DepartmentSummary>("/departments", "POST", payload, true);
  }

  async updateDepartment(departmentId: string, name: string): Promise<DepartmentSummary> {
    return this.json<DepartmentSummary>(
      `/departments/${encodeURIComponent(departmentId)}`,
      "PUT",
      { name },
      true,
    );
  }

  async getDepartmentDeactivationImpact(departmentId: string): Promise<DepartmentImpact> {
    return this.request<DepartmentImpact>(
      `/departments/${encodeURIComponent(departmentId)}/deactivation-impact`,
    );
  }

  async deactivateDepartment(departmentId: string): Promise<DepartmentSummary> {
    return this.json<DepartmentSummary>(
      `/departments/${encodeURIComponent(departmentId)}/deactivate`,
      "POST",
      {},
      true,
    );
  }

  async reactivateDepartment(departmentId: string): Promise<DepartmentSummary> {
    return this.json<DepartmentSummary>(
      `/departments/${encodeURIComponent(departmentId)}/reactivate`,
      "POST",
      {},
      true,
    );
  }

  async generateTemporaryPassword(username?: string): Promise<string> {
    const suffix = username ? `?username=${encodeURIComponent(username)}` : "";
    const response = await this.request<{ temporary_password: string }>(
      `/users/temporary-password${suffix}`,
    );
    return response.temporary_password;
  }

  async createUser(payload: {
    username: string;
    display_name: string;
    temporary_password: string;
    role: UserRole;
    primary_department_id: string | null;
  }): Promise<TemporaryCredential> {
    return this.json<TemporaryCredential>("/users", "POST", { ...payload }, true);
  }

  async disableUser(userId: string): Promise<ManagedUser> {
    return this.json<ManagedUser>(`/users/${encodeURIComponent(userId)}/disable`, "POST", {}, true);
  }

  async archiveUser(userId: string): Promise<ManagedUser> {
    return this.json<ManagedUser>(`/users/${encodeURIComponent(userId)}/archive`, "POST", {}, true);
  }

  async reactivateUser(userId: string): Promise<ManagedUser> {
    return this.json<ManagedUser>(
      `/users/${encodeURIComponent(userId)}/reactivate`,
      "POST",
      {},
      true,
    );
  }

  async updateUserProfile(
    userId: string,
    payload: { display_name: string; primary_department_id: string | null },
  ): Promise<ManagedUser> {
    return this.json<ManagedUser>(
      `/users/${encodeURIComponent(userId)}/profile`,
      "PUT",
      payload,
      true,
    );
  }

  async resetUserPassword(
    userId: string,
    temporaryPassword: string,
    stepUpCode: string,
  ): Promise<TemporaryCredential> {
    return this.json<TemporaryCredential>(
      `/users/${encodeURIComponent(userId)}/password-reset`,
      "POST",
      { temporary_password: temporaryPassword, step_up_code: stepUpCode },
      true,
    );
  }

  async changeUserRole(userId: string, role: UserRole): Promise<ManagedUser> {
    return this.json<ManagedUser>(
      `/users/${encodeURIComponent(userId)}/role`,
      "PUT",
      { role },
      true,
    );
  }

  async resetUserMfa(userId: string, stepUpCode: string): Promise<ManagedUser> {
    return this.json<ManagedUser>(
      `/users/${encodeURIComponent(userId)}/mfa-reset`,
      "POST",
      { step_up_code: stepUpCode },
      true,
    );
  }

  async listUserGrants(userId: string): Promise<ResourceGrant[]> {
    return this.request<ResourceGrant[]>(`/users/${encodeURIComponent(userId)}/grants`);
  }

  async addUserGrant(
    userId: string,
    resourceType: ResourceType,
    resourceId: string,
  ): Promise<ResourceGrant> {
    return this.json<ResourceGrant>(
      `/users/${encodeURIComponent(userId)}/grants`,
      "POST",
      { resource_type: resourceType, resource_id: resourceId },
      true,
    );
  }

  async revokeUserGrant(userId: string, grantId: string): Promise<ResourceGrant> {
    return this.json<ResourceGrant>(
      `/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(grantId)}`,
      "DELETE",
      {},
      true,
    );
  }

  async listManagedImportProfiles(): Promise<ManagedImportProfile[]> {
    return this.request<ManagedImportProfile[]>("/import-profiles");
  }

  async createImportProfile(payload: {
    code: string;
    display_name: string;
    definition: Record<string, unknown>;
  }): Promise<ManagedImportProfile> {
    return this.json<ManagedImportProfile>("/import-profiles", "POST", { ...payload }, true);
  }

  async createImportProfileVersion(
    profileId: string,
    definition: Record<string, unknown>,
  ): Promise<ManagedImportProfile> {
    return this.json<ManagedImportProfile>(
      `/import-profiles/${encodeURIComponent(profileId)}/versions`,
      "POST",
      { definition },
      true,
    );
  }

  async publishImportProfile(versionId: string): Promise<ManagedImportProfile> {
    return this.json<ManagedImportProfile>(
      `/import-profile-versions/${encodeURIComponent(versionId)}/publish`,
      "POST",
      {},
      true,
    );
  }

  async disableImportProfile(versionId: string): Promise<ManagedImportProfile> {
    return this.json<ManagedImportProfile>(
      `/import-profile-versions/${encodeURIComponent(versionId)}/disable`,
      "POST",
      {},
      true,
    );
  }

  async listQuotas(): Promise<Quota[]> {
    return this.request<Quota[]>("/quotas");
  }

  async upsertQuota(
    subjectType: Quota["subject_type"],
    subjectId: string,
    period: Quota["period"],
    payload: QuotaDraft,
  ): Promise<Quota> {
    return this.json<Quota>(
      `/quotas/${subjectType}/${encodeURIComponent(subjectId)}/${period}`,
      "PUT",
      { ...payload },
      true,
    );
  }

  async disableQuota(
    subjectType: Quota["subject_type"],
    subjectId: string,
    period: Quota["period"],
  ): Promise<Quota> {
    return this.json<Quota>(
      `/quotas/${subjectType}/${encodeURIComponent(subjectId)}/${period}`,
      "DELETE",
      {},
      true,
    );
  }

  async listProviders(): Promise<ModelProvider[]> {
    return this.request<ModelProvider[]>("/providers");
  }

  async listProviderAdapters(): Promise<ProviderAdapterDescriptor[]> {
    return this.request<ProviderAdapterDescriptor[]>("/provider-adapters");
  }

  async getProviderReadiness(providerId: string): Promise<ProviderReadiness> {
    return this.request<ProviderReadiness>(
      `/providers/${encodeURIComponent(providerId)}/readiness`,
    );
  }

  async listProviderPolicies(providerId: string): Promise<ProviderPolicy[]> {
    return this.request<ProviderPolicy[]>(`/providers/${encodeURIComponent(providerId)}/policies`);
  }

  async createProvider(payload: {
    code: string;
    display_name: string;
    adapter_type: string;
    endpoint_url: string | null;
    capabilities: string[];
    contract_metadata: Record<string, unknown>;
    credentials: Record<string, string> | null;
    adapter_options?: Record<string, unknown>;
  }): Promise<ModelProvider> {
    return this.json<ModelProvider>("/providers", "POST", { ...payload }, true);
  }

  async verifyProviderConnection(providerId: string): Promise<{
    provider: ModelProvider;
    result_code: string;
    discovered_model_count: number;
  }> {
    return this.json(
      `/providers/${encodeURIComponent(providerId)}/verify`,
      "POST",
      {},
      true,
    );
  }

  async updateProviderConnection(
    providerId: string,
    payload: {
      endpoint_url: string | null;
      adapter_options: Record<string, unknown>;
    },
  ): Promise<ModelProvider> {
    return this.json<ModelProvider>(
      `/providers/${encodeURIComponent(providerId)}/connection`,
      "PUT",
      payload,
      true,
    );
  }

  async listProviderModels(providerId: string): Promise<ProviderModel[]> {
    return this.request<ProviderModel[]>(
      `/providers/${encodeURIComponent(providerId)}/models`,
    );
  }

  async discoverProviderModels(providerId: string): Promise<ProviderModel[]> {
    return this.json<ProviderModel[]>(
      `/providers/${encodeURIComponent(providerId)}/models/discover`,
      "POST",
      {},
      true,
    );
  }

  async registerProviderModel(
    providerId: string,
    payload: {
      model_id: string;
      display_name: string;
      capabilities: string[];
      context_window: number | null;
      max_output_tokens: number | null;
      pricing_metadata: Record<string, unknown>;
      pricing_version: string;
    },
  ): Promise<ProviderModel> {
    return this.json<ProviderModel>(
      `/providers/${encodeURIComponent(providerId)}/models`,
      "POST",
      payload,
      true,
    );
  }

  async setProviderModelState(
    modelId: string,
    action: "validate" | "enable" | "disable",
  ): Promise<ProviderModel> {
    return this.json<ProviderModel>(
      `/provider-models/${encodeURIComponent(modelId)}/${action}`,
      "POST",
      {},
      true,
    );
  }

  async updateProviderModel(
    modelId: string,
    payload: {
      model_id: string;
      display_name: string;
      capabilities: string[];
      context_window: number | null;
      max_output_tokens: number | null;
      pricing_metadata: Record<string, unknown>;
      pricing_version: string;
    },
  ): Promise<ProviderModel> {
    return this.json<ProviderModel>(
      `/provider-models/${encodeURIComponent(modelId)}`,
      "PUT",
      payload,
      true,
    );
  }

  async listProviderModelRevisions(modelId: string): Promise<Array<Record<string, unknown>>> {
    return this.request<Array<Record<string, unknown>>>(
      `/provider-models/${encodeURIComponent(modelId)}/revisions`,
    );
  }

  async listModelRoutes(): Promise<ModelRoute[]> {
    return this.request<ModelRoute[]>("/model-routes");
  }

  async listOperationalModelRoutes(): Promise<OperationalModelRoute[]> {
    return this.request<OperationalModelRoute[]>("/operational-model-routes");
  }

  async createModelRoute(payload: {
    code: string;
    display_name: string;
    capability: string;
    description: string;
    candidate_ids: string[];
  }): Promise<ModelRoute> {
    return this.json<ModelRoute>("/model-routes", "POST", payload, true);
  }

  async createModelRouteVersion(
    routeId: string,
    candidateIds: string[],
  ): Promise<ModelRouteVersion> {
    return this.json<ModelRouteVersion>(
      `/model-routes/${encodeURIComponent(routeId)}/versions`,
      "POST",
      { candidate_ids: candidateIds },
      true,
    );
  }

  async setModelRouteVersionState(
    versionId: string,
    action: "publish" | "disable",
  ): Promise<ModelRouteVersion> {
    return this.json<ModelRouteVersion>(
      `/model-route-versions/${encodeURIComponent(versionId)}/${action}`,
      "POST",
      {},
      true,
    );
  }

  async rotateProviderCredentials(
    providerId: string,
    credentials: Record<string, string>,
  ): Promise<ModelProvider> {
    return this.json<ModelProvider>(
      `/providers/${encodeURIComponent(providerId)}/credentials`,
      "PUT",
      { credentials },
      true,
    );
  }

  async createProviderPolicy(
    providerId: string,
    payload: {
      allowed_classifications: ProviderPolicy["allowed_classifications"];
      allowed_capabilities: string[];
      allowed_regions: string[];
      max_cost_per_run: string;
      policy_document: Record<string, unknown>;
    },
  ): Promise<ProviderPolicy> {
    return this.json<ProviderPolicy>(
      `/providers/${encodeURIComponent(providerId)}/policies`,
      "POST",
      { ...payload },
      true,
    );
  }

  async publishProviderPolicy(policyId: string): Promise<ProviderPolicy> {
    return this.json<ProviderPolicy>(
      `/provider-policies/${encodeURIComponent(policyId)}/publish`,
      "POST",
      {},
      true,
    );
  }

  async setProviderState(
    providerId: string,
    action: "activate" | "disable",
  ): Promise<ModelProvider> {
    return this.json<ModelProvider>(
      `/providers/${encodeURIComponent(providerId)}/${action}`,
      "POST",
      {},
      true,
    );
  }

  async searchAuditEvents(
    filters: {
      action?: string;
      outcome?: AuditEvent["outcome"] | "";
      actor_user_id?: string;
      started_from?: string;
      started_until?: string;
      limit?: number;
    } = {},
  ): Promise<AuditEvent[]> {
    const query = queryString(filters);
    return this.request<AuditEvent[]>(`/governance/audit-events${query}`);
  }

  async getUsageReport(
    filters: {
      started_from?: string;
      started_until?: string;
      provider_id?: string;
      department_id?: string;
      user_id?: string;
      skill_identifier?: string;
    } = {},
  ): Promise<UsageReport> {
    const query = queryString(filters);
    return this.request<UsageReport>(`/governance/usage${query}`);
  }

  async getGovernanceHealth(): Promise<GovernanceHealth> {
    return this.request<GovernanceHealth>("/governance/health");
  }

  async listImportProfiles(): Promise<ImportProfile[]> {
    return this.request<ImportProfile[]>("/import-profiles/published");
  }

  async listDataSources(): Promise<DataSource[]> {
    return this.request<DataSource[]>("/data-sources");
  }

  async uploadRaw(dataSourceId: string, file: File): Promise<RawUpload> {
    const body = new FormData();
    body.set("file", file);
    return this.request<RawUpload>(`/uploads?data_source_id=${encodeURIComponent(dataSourceId)}`, {
      method: "POST",
      body,
      headers: csrfHeaders(),
    });
  }

  async importReportSet(payload: Record<string, unknown>): Promise<ReportSetImportResponse> {
    return this.json<ReportSetImportResponse>("/report-sets/import", "POST", payload, true);
  }

  async startWeeklyReport(reportSetId: string): Promise<RunStatus> {
    return this.json<RunStatus>(
      "/runs/weekly-report",
      "POST",
      { request_id: crypto.randomUUID(), report_set_id: reportSetId },
      true,
    );
  }

  async getRun(runId: string): Promise<RunStatus> {
    return this.request<RunStatus>(`/runs/${encodeURIComponent(runId)}`);
  }

  async listTaskCards(): Promise<TaskCard[]> {
    return this.request<TaskCard[]>("/execution/task-cards");
  }

  async getCapabilityCatalog(): Promise<CapabilityCatalog> {
    return this.request<CapabilityCatalog>("/capabilities");
  }

  async startTaskCard(payload: {
    task_card: string;
    scope_type: string;
    scope_id: string;
    input_payload: Record<string, unknown>;
  }): Promise<ExecutionRun> {
    return this.json<ExecutionRun>(
      "/execution/runs",
      "POST",
      { request_id: crypto.randomUUID(), ...payload },
      true,
    );
  }

  async listExecutionRuns(): Promise<ExecutionRun[]> {
    return this.request<ExecutionRun[]>("/execution/runs");
  }

  async getExecutionRun(runId: string): Promise<ExecutionRunDetail> {
    return this.request<ExecutionRunDetail>(`/execution/runs/${encodeURIComponent(runId)}`);
  }

  async cancelExecutionRun(runId: string): Promise<ExecutionRun> {
    return this.json<ExecutionRun>(
      `/execution/runs/${encodeURIComponent(runId)}/cancel`,
      "POST",
      {},
      true,
    );
  }

  async decideExecutionApproval(
    runId: string,
    approvalKey: string,
    decision: "approved" | "rejected",
    note: string,
  ): Promise<ExecutionApproval> {
    return this.json<ExecutionApproval>(
      `/execution/runs/${encodeURIComponent(runId)}/approvals/${encodeURIComponent(approvalKey)}`,
      "POST",
      { decision, note: note.trim() || null },
      true,
    );
  }

  async issueWebSocketTicket(): Promise<WebSocketTicket> {
    return this.json<WebSocketTicket>("/ws/tickets", "POST", {}, true);
  }

  runWebSocketUrl(runId: string, afterSeq: number, ticket: string): string {
    const base = new URL(this.baseUrl, window.location.origin);
    base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
    base.pathname = `${base.pathname.replace(/\/$/, "")}/ws`;
    base.search = new URLSearchParams({
      ticket,
      run_id: runId,
      after_seq: String(afterSeq),
    }).toString();
    return base.toString();
  }

  async getSummary(snapshotId: string): Promise<WeeklySummary> {
    return this.request<WeeklySummary>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/summary`,
    );
  }

  async listFindings(snapshotId: string): Promise<Finding[]> {
    return this.request<Finding[]>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/findings`,
    );
  }

  async listSearchTerms(snapshotId: string): Promise<AmazonSearchTerm[]> {
    return this.request<AmazonSearchTerm[]>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/search-terms`,
    );
  }

  async listAdvertisingCandidates(snapshotId: string): Promise<AdvertisingAdvisory> {
    return this.request<AdvertisingAdvisory>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/advertising-candidates`,
    );
  }

  async validateNarrative(
    snapshotId: string,
    payload: Record<string, unknown>,
  ): Promise<NarrativeValidation> {
    return this.json<NarrativeValidation>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/narrative/validate`,
      "POST",
      payload,
      true,
    );
  }

  async listReplenishmentPolicies(): Promise<ReplenishmentPolicy[]> {
    return this.request<ReplenishmentPolicy[]>("/replenishment-policies/published");
  }

  async listManagedReplenishmentPolicies(scopeId: string): Promise<ReplenishmentPolicy[]> {
    return this.request<ReplenishmentPolicy[]>(
      `/replenishment-policies?scope_id=${encodeURIComponent(scopeId)}`,
    );
  }

  async createReplenishmentPolicy(payload: ReplenishmentPolicyDraft): Promise<ReplenishmentPolicy> {
    return this.json<ReplenishmentPolicy>("/replenishment-policies", "POST", { ...payload }, true);
  }

  async createReplenishmentPolicyVersion(
    policyId: string,
    payload: ReplenishmentPolicyDraft,
  ): Promise<ReplenishmentPolicy> {
    return this.json<ReplenishmentPolicy>(
      `/replenishment-policies/${encodeURIComponent(policyId)}/versions`,
      "POST",
      { ...payload },
      true,
    );
  }

  async publishReplenishmentPolicy(policyId: string): Promise<ReplenishmentPolicy> {
    return this.json<ReplenishmentPolicy>(
      `/replenishment-policies/${encodeURIComponent(policyId)}/publish`,
      "POST",
      {},
      true,
    );
  }

  async startReplenishment(payload: {
    scope_id: string;
    policy_code: string;
    dataset_version_ids: string[];
    destination_id: string;
  }): Promise<ReplenishmentRun> {
    return this.json<ReplenishmentRun>(
      "/runs/replenishment",
      "POST",
      { request_id: crypto.randomUUID(), ...payload },
      true,
    );
  }

  async listReplenishmentRecommendations(scopeId?: string): Promise<ReplenishmentRecommendation[]> {
    const query = scopeId ? `?scope_id=${encodeURIComponent(scopeId)}` : "";
    return this.request<ReplenishmentRecommendation[]>(`/replenishment/recommendations${query}`);
  }

  async reviewReplenishmentRecommendation(
    resultId: string,
    decision: "accepted" | "rejected",
  ): Promise<ReplenishmentRecommendation> {
    return this.json<ReplenishmentRecommendation>(
      `/replenishment/recommendations/${encodeURIComponent(resultId)}/review`,
      "POST",
      { decision },
      true,
    );
  }

  replenishmentArtifactUrl(artifactId: string): string {
    return `${this.baseUrl}/replenishment/artifacts/${encodeURIComponent(artifactId)}/download`;
  }

  async uploadImageSource(payload: ImageSourceDraft, file: File): Promise<ImagePilotRequest> {
    const body = new FormData();
    body.set("file", file);
    body.set("data_source_id", payload.data_source_id);
    body.set("request_key", payload.request_key);
    body.set("site_id", payload.site_id);
    body.set("intended_use", payload.intended_use);
    body.set("dimensions", JSON.stringify(payload.dimensions));
    body.set("brand_rules", JSON.stringify(payload.brand_rules));
    body.set("rights_context", JSON.stringify(payload.rights_context));
    body.set("prompt_template_version", payload.prompt_template_version);
    body.set("user_inputs", JSON.stringify(payload.user_inputs));
    return this.request<ImagePilotRequest>("/images/requests", {
      method: "POST",
      body,
      headers: csrfHeaders(),
    });
  }

  async listImageRequests(): Promise<ImagePilotRequest[]> {
    return this.request<ImagePilotRequest[]>("/images/requests");
  }

  async startImageGeneration(
    imageRequestId: string,
    payload: {
      prompt: string;
      model: string;
      candidate_count: number;
      size: string;
      region: string;
      classification: "internal" | "confidential";
      max_cost: string;
    },
  ): Promise<ImagePilotRun> {
    return this.json<ImagePilotRun>(
      `/images/requests/${encodeURIComponent(imageRequestId)}/runs`,
      "POST",
      { request_id: crypto.randomUUID(), ...payload },
      true,
    );
  }

  async listImageCandidates(imageRequestId?: string): Promise<ImagePilotCandidate[]> {
    const query = imageRequestId ? `?image_request_id=${encodeURIComponent(imageRequestId)}` : "";
    return this.request<ImagePilotCandidate[]>(`/images/candidates${query}`);
  }

  async getCatalogContexts(storeId?: string): Promise<CatalogContexts> {
    return this.request<CatalogContexts>(`/products/contexts${queryString({ store_id: storeId })}`);
  }

  async listCatalogProducts(filters: {
    storeId: string;
    responsibleUserId?: string;
    page: number;
    pageSize: 20 | 50 | 100;
    signal?: AbortSignal;
  }): Promise<CatalogProductPage> {
    return this.request<CatalogProductPage>(
      `/products${queryString({
        store_id: filters.storeId,
        responsible_user_id: filters.responsibleUserId,
        page: filters.page,
        page_size: filters.pageSize,
      })}`,
      { signal: filters.signal },
    );
  }

  async createCatalogProduct(payload: {
    storeId: string;
    sku: string;
    asin: string;
    name: string;
    image: File | null;
  }): Promise<CatalogProduct> {
    const form = new FormData();
    form.append("store_id", payload.storeId);
    form.append("sku", payload.sku);
    form.append("asin", payload.asin);
    form.append("name", payload.name);
    if (payload.image) form.append("image", payload.image);
    return this.form<CatalogProduct>("/products", form);
  }

  async importCatalogCsv(storeId: string, file: File): Promise<CatalogImportResult> {
    const form = new FormData();
    form.append("store_id", storeId);
    form.append("file", file);
    return this.form<CatalogImportResult>("/products/imports", form);
  }

  async importCatalogImages(
    storeId: string,
    mappings: CatalogImageMapping[],
  ): Promise<CatalogImageImportItem[]> {
    const form = new FormData();
    form.append("store_id", storeId);
    for (const mapping of mappings) {
      form.append("skus", mapping.sku);
      form.append("files", mapping.file);
    }
    const response = await this.form<{ items: CatalogImageImportItem[] }>(
      "/products/image-imports",
      form,
    );
    return response.items;
  }

  async deleteCatalogProducts(storeId: string, productIds: string[]): Promise<number> {
    const response = await this.json<{ deleted_count: number }>(
      "/products/batch-delete",
      "POST",
      { store_id: storeId, product_ids: productIds },
      true,
    );
    return response.deleted_count;
  }

  async exportCatalogProducts(storeId: string, responsibleUserId?: string): Promise<Blob> {
    const path = `/products/export${queryString({
      store_id: storeId,
      responsible_user_id: responsibleUserId,
    })}`;
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      credentials: "same-origin",
      headers: { Accept: "text/csv" },
    });
    if (!response.ok) await this.throwResponseError(response);
    return response.blob();
  }

  catalogProductImageUrl(productId: string, variant: "thumbnail" | "preview"): string {
    return `${this.baseUrl}/products/${encodeURIComponent(productId)}/images/${variant}`;
  }

  async reviewImageCandidate(
    candidateId: string,
    decision: "accepted" | "rejected",
    annotation: string,
  ): Promise<{ id: string; decision: "accepted" | "rejected" }> {
    return this.json(
      `/images/candidates/${encodeURIComponent(candidateId)}/review`,
      "POST",
      { decision, annotation: annotation.trim() || null },
      true,
    );
  }

  imageCandidateUrl(candidateId: string): string {
    return `${this.baseUrl}/images/candidates/${encodeURIComponent(candidateId)}/download`;
  }

  async reviewFinding(
    snapshotId: string,
    findingKey: string,
    decision: "accepted" | "rejected",
    note: string,
  ): Promise<ReviewResponse> {
    return this.json<ReviewResponse>(
      `/analysis-snapshots/${encodeURIComponent(snapshotId)}/findings/${encodeURIComponent(findingKey)}/review`,
      "POST",
      { decision, note: note.trim() || null },
      true,
    );
  }

  artifactDownloadUrl(artifactId: string): string {
    return `${this.baseUrl}/artifacts/${encodeURIComponent(artifactId)}/export`;
  }

  profileSampleUrl(versionId: string): string {
    return `${this.baseUrl}/import-profiles/${encodeURIComponent(versionId)}/sample`;
  }

  private async json<T>(
    path: string,
    method: string,
    body: Record<string, unknown>,
    csrf = false,
  ): Promise<T> {
    const headers = new Headers({ "Content-Type": "application/json" });
    if (csrf) {
      csrfHeaders(headers);
    }
    return this.request<T>(path, { method, headers, body: JSON.stringify(body) });
  }

  private async form<T>(path: string, body: FormData): Promise<T> {
    const headers = csrfHeaders();
    return this.request<T>(path, { method: "POST", headers, body });
  }

  private async request<T>(
    path: string,
    init: RequestInit = {},
    notifySessionExpiry = true,
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "same-origin",
      headers,
    });
    if (!response.ok) await this.throwResponseError(response, notifySessionExpiry);
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private async throwResponseError(response: Response, notifySessionExpiry = true): Promise<never> {
    const body = await readErrorBody(response);
    if (notifySessionExpiry && body.error?.code === "authentication_required") {
      window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
    }
    throw new ApiError(
      body.error?.message ?? body.detail ?? `请求失败（HTTP ${response.status}）`,
      response.status,
      body.request_id ?? response.headers.get("X-Request-ID") ?? undefined,
      body.error?.code,
      body.error?.issues,
    );
  }
}

function csrfHeaders(headers: Headers = new Headers()): Headers {
  const token = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith("ecom_csrf="))
    ?.slice("ecom_csrf=".length);
  if (token) {
    headers.set("X-CSRF-Token", decodeURIComponent(token));
  }
  return headers;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return {};
  }
}

function queryString(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && String(value).trim()) params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const apiClient = new ApiClient();
